import { useCallback } from 'react'
import { useContentCpStore } from '@/stores/useContentCpStore'
import { streamContentCpCardIngestion } from '@/lib/api'
import { SourceQueue } from './SourceQueue'
import { WorkflowLogPanel } from './WorkflowLogPanel'
import { DropZone } from './DropZone'

export function ContentCpEditor() {
  const {
    sessionId,
    sourceQueue,
    batchState,
    startBatch,
    completeBatch,
    setCurrentSourceIndex,
    updateSourceStatus,
    appendLogEntry,
    addSessionCards,
  } = useContentCpStore()

  const runBatch = useCallback(async () => {
    const queuedSources = sourceQueue.filter((s) => s.status === 'queued')
    if (queuedSources.length === 0) return

    startBatch()
    const batchStartTime = Date.now()

    // Log batch start
    appendLogEntry({
      ts: new Date().toISOString(),
      elapsed_ms: 0,
      source_index: -1,
      type: 'workflow.start',
      level: 'milestone',
      message: `Starting batch processing of ${queuedSources.length} source(s)...`,
    })

    let totalCards = 0
    let totalEdges = 0

    for (let i = 0; i < queuedSources.length; i++) {
      const source = queuedSources[i]
      setCurrentSourceIndex(i)

      // Mark as processing
      updateSourceStatus(source.id, 'processing', {
        startedAt: new Date().toISOString(),
      })

      appendLogEntry({
        ts: new Date().toISOString(),
        elapsed_ms: Date.now() - batchStartTime,
        source_index: i,
        type: 'source.process.start',
        level: 'milestone',
        message: `[${i + 1}/${queuedSources.length}] Processing: ${source.filename || source.textPreview || 'source'}`,
      })

      try {
        // Prepare params based on source type
        const params: {
          session_id?: string
          raw_text?: string
          title?: string
          images?: Array<{ data: string; mimeType: string; name: string }>
        } = {
          session_id: sessionId || undefined,
          title: source.title,
        }

        // For text sources
        if (source.type === 'text' && source.text) {
          params.raw_text = source.text
        }

        // For image sources
        if (source.type === 'image' && source.images) {
          params.images = source.images
        }

        // For file sources, read the file
        let file: File | null = null
        if (source.type === 'file' && source.file) {
          file = source.file
        }

        // Process via streaming API
        await streamContentCpCardIngestion(
          params,
          file,
          {
            onExtractionStart: () => {
              appendLogEntry({
                ts: new Date().toISOString(),
                elapsed_ms: Date.now() - batchStartTime,
                source_index: i,
                type: 'source.extract.start',
                level: 'progress',
                message: 'Extracting text...',
              })
            },
            onExtractionComplete: () => {
              appendLogEntry({
                ts: new Date().toISOString(),
                elapsed_ms: Date.now() - batchStartTime,
                source_index: i,
                type: 'source.extract.complete',
                level: 'progress',
                message: 'Text extracted',
              })
            },
            onIngestStart: () => {
              appendLogEntry({
                ts: new Date().toISOString(),
                elapsed_ms: Date.now() - batchStartTime,
                source_index: i,
                type: 'source.ingest.start',
                level: 'progress',
                message: 'Ingesting content...',
              })
            },
            onIngestComplete: (sourceId) => {
              appendLogEntry({
                ts: new Date().toISOString(),
                elapsed_ms: Date.now() - batchStartTime,
                source_index: i,
                type: 'source.ingest.complete',
                level: 'progress',
                message: `Source saved: ${sourceId.slice(0, 8)}...`,
                data: { sourceId },
              })
            },
            onDuplicateDetected: (sourceId) => {
              appendLogEntry({
                ts: new Date().toISOString(),
                elapsed_ms: Date.now() - batchStartTime,
                source_index: i,
                type: 'source.duplicate',
                level: 'milestone',
                message: `Duplicate detected (existing: ${sourceId.slice(0, 8)}...)`,
                data: { sourceId },
              })
            },
            onCardifyStart: () => {
              appendLogEntry({
                ts: new Date().toISOString(),
                elapsed_ms: Date.now() - batchStartTime,
                source_index: i,
                type: 'cardify.start',
                level: 'progress',
                message: 'Extracting cards...',
              })
            },
            onCardProposed: (card, current, total) => {
              appendLogEntry({
                ts: new Date().toISOString(),
                elapsed_ms: Date.now() - batchStartTime,
                source_index: i,
                type: 'cardify.card.proposed',
                level: 'detail',
                message: `[${current}/${total}] ${card?.type || 'card'}: "${card?.title || 'untitled'}"`,
                data: { card, current, total },
              })
            },
            onCardifyComplete: (count) => {
              appendLogEntry({
                ts: new Date().toISOString(),
                elapsed_ms: Date.now() - batchStartTime,
                source_index: i,
                type: 'cardify.complete',
                level: 'milestone',
                message: `Extracted ${count} cards`,
                data: { count },
              })
            },
            onEmbedStart: () => {
              appendLogEntry({
                ts: new Date().toISOString(),
                elapsed_ms: Date.now() - batchStartTime,
                source_index: i,
                type: 'embed.start',
                level: 'progress',
                message: 'Generating embeddings...',
              })
            },
            onEmbedComplete: () => {
              appendLogEntry({
                ts: new Date().toISOString(),
                elapsed_ms: Date.now() - batchStartTime,
                source_index: i,
                type: 'embed.complete',
                level: 'progress',
                message: 'Embeddings generated',
              })
            },
            onCanonicalizeStart: () => {
              appendLogEntry({
                ts: new Date().toISOString(),
                elapsed_ms: Date.now() - batchStartTime,
                source_index: i,
                type: 'canon.start',
                level: 'progress',
                message: 'Deduplicating...',
              })
            },
            onCanonicalizeDecision: (card, current, total) => {
              appendLogEntry({
                ts: new Date().toISOString(),
                elapsed_ms: Date.now() - batchStartTime,
                source_index: i,
                type: 'canon.card.decision',
                level: 'detail',
                message: `[${current}/${total}] Processing card...`,
                data: { card, current, total },
              })
            },
            onCanonicalizeComplete: (newCount, dupCount, relatedCount) => {
              appendLogEntry({
                ts: new Date().toISOString(),
                elapsed_ms: Date.now() - batchStartTime,
                source_index: i,
                type: 'canon.complete',
                level: 'milestone',
                message: `Deduplication: ${newCount} new, ${dupCount} duplicates, ${relatedCount} related`,
                data: { newCount, dupCount, relatedCount },
              })
            },
            onLinkStart: () => {
              appendLogEntry({
                ts: new Date().toISOString(),
                elapsed_ms: Date.now() - batchStartTime,
                source_index: i,
                type: 'link.start',
                level: 'progress',
                message: 'Creating links...',
              })
            },
            onLinkCreated: (edge) => {
              appendLogEntry({
                ts: new Date().toISOString(),
                elapsed_ms: Date.now() - batchStartTime,
                source_index: i,
                type: 'link.edge.create',
                level: 'detail',
                message: `Edge: ${edge?.type || 'link'}`,
                data: { edge },
              })
            },
            onLinkComplete: (edgesCreated) => {
              appendLogEntry({
                ts: new Date().toISOString(),
                elapsed_ms: Date.now() - batchStartTime,
                source_index: i,
                type: 'link.complete',
                level: 'milestone',
                message: `Created ${edgesCreated} edges`,
                data: { edgesCreated },
              })
            },
            onDone: (summary) => {
              totalCards += summary.cards_created
              totalEdges += summary.edges_created

              // Add cards to session
              if (summary.card_ids && summary.card_ids.length > 0) {
                addSessionCards(summary.card_ids)
              }

              updateSourceStatus(source.id, 'complete', {
                sourceId: summary.source_id,
                cardsCreated: summary.cards_created,
                edgesCreated: summary.edges_created,
                completedAt: new Date().toISOString(),
              })

              appendLogEntry({
                ts: new Date().toISOString(),
                elapsed_ms: Date.now() - batchStartTime,
                source_index: i,
                type: 'source.process.complete',
                level: 'milestone',
                message: `Complete: ${summary.cards_created} cards, ${summary.edges_created} edges`,
                data: summary,
              })
            },
            onError: (error) => {
              updateSourceStatus(source.id, 'error', {
                error,
                completedAt: new Date().toISOString(),
              })

              appendLogEntry({
                ts: new Date().toISOString(),
                elapsed_ms: Date.now() - batchStartTime,
                source_index: i,
                type: 'source.process.error',
                level: 'error',
                message: `Error: ${error}`,
                data: { error },
              })
            },
          }
        )
      } catch (e) {
        const errorMsg = e instanceof Error ? e.message : 'Unknown error'
        updateSourceStatus(source.id, 'error', {
          error: errorMsg,
          completedAt: new Date().toISOString(),
        })

        appendLogEntry({
          ts: new Date().toISOString(),
          elapsed_ms: Date.now() - batchStartTime,
          source_index: i,
          type: 'source.process.error',
          level: 'error',
          message: `Error: ${errorMsg}`,
        })
      }
    }

    // Batch complete
    completeBatch()

    appendLogEntry({
      ts: new Date().toISOString(),
      elapsed_ms: Date.now() - batchStartTime,
      source_index: -1,
      type: 'workflow.complete',
      level: 'milestone',
      message: `Batch complete! Total: ${totalCards} cards, ${totalEdges} edges`,
      data: { totalCards, totalEdges, duration_ms: Date.now() - batchStartTime },
    })
  }, [
    sessionId,
    sourceQueue,
    startBatch,
    completeBatch,
    setCurrentSourceIndex,
    updateSourceStatus,
    appendLogEntry,
    addSessionCards,
  ])

  const hasContent = sourceQueue.length > 0

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Main content area - full height flex layout */}
      <div className="flex-1 flex flex-col min-h-0 p-3 md:p-4 pb-4 gap-3">
        {/* Drop Zone - shrinks when we have content, otherwise fills space */}
        <div className={hasContent ? 'flex-shrink-0' : 'flex-1 flex items-center justify-center'}>
          <DropZone disabled={batchState.isProcessing} />
        </div>

        {/* Source Queue - takes up space proportionally when we have content */}
        {hasContent && (
          <SourceQueue
            onRunBatch={runBatch}
            className="flex-shrink-0 max-h-[40vh] md:max-h-[35vh]"
          />
        )}

        {/* Workflow Log - fills remaining space when expanded */}
        <WorkflowLogPanel className="flex-1 min-h-[200px] md:min-h-[300px]" />
      </div>
    </div>
  )
}
